import React, { useState, useMemo } from 'react';
import { PageShell } from '../../components/Ui';
import { useHmsRecords } from '../../hooks/useHmsRecords';
import { useLiveData } from '../../hooks/useLiveData';
import { ipApi } from '../../services/api';
import { buildKQIs } from '../../utils/qualityKpis';

/* ═══════════════════════════════════════════════════════════
   QUALITY / NABH AUDIT MODULE
   - Incident Reporting System
   - NABH Standard-wise Audit Checklist
   - Quality Indicator Dashboard
   - Corrective Action (CAPA) Tracker
   - Mock Drill & Accreditation Readiness
   - Monthly Quality Committee Report
═══════════════════════════════════════════════════════════ */

const NABH_STANDARDS = [
  { code: 'ACC', name: 'Access, Assessment & Continuity of Care', chapters: 9 },
  { code: 'COP', name: 'Care of Patients', chapters: 7 },
  { code: 'MOM', name: 'Management of Medication', chapters: 6 },
  { code: 'PRE', name: 'Patient Rights & Education', chapters: 5 },
  { code: 'HIC', name: 'Hospital Infection Control', chapters: 6 },
  { code: 'CQI', name: 'Continuous Quality Improvement', chapters: 6 },
  { code: 'ROM', name: 'Responsibilities of Management', chapters: 5 },
  { code: 'HRM', name: 'Human Resource Management', chapters: 6 },
  { code: 'FMS', name: 'Facility Management & Safety', chapters: 7 },
  { code: 'IMS', name: 'Information Management System', chapters: 5 },
];

const NABH_CHECKLIST = [
  { item: 'Patient identification with two identifiers', met: false },
  { item: 'Informed consent obtained before procedure', met: false },
  { item: 'Medication reconciliation done on admission', met: false },
  { item: 'Allergy documentation in patient record', met: false },
  { item: 'Fall risk assessment done within 24 hrs', met: false },
  { item: 'Discharge summary provided to patient', met: false },
  { item: 'Patient education on diagnosis & medications', met: false },
  { item: 'Pain assessment documented every 4 hrs', met: false },
];

function scoreColor(s) {
  return s >= 90 ? '#10b981' : s >= 75 ? '#f59e0b' : '#dc2626';
}

const INCIDENT_CATEGORIES = ['Medication Error', 'Patient Fall', 'Procedure Complication', 'Equipment Failure', 'Diagnostic Error', 'Blood Transfusion Reaction', 'Healthcare-Associated Infection', 'Security Incident', 'Near Miss', 'Sentinel Event', 'Complaint / Grievance', 'Other'];
const SEVERITY_LEVELS = [{ val: 'Near Miss', color: '#10b981', desc: 'No harm, caught before reaching patient' }, { val: 'Minor', color: '#06b6d4', desc: 'Reached patient, minimal/no harm' }, { val: 'Moderate', color: '#f59e0b', desc: 'Required additional treatment' }, { val: 'Major', color: '#f97316', desc: 'Caused serious harm, prolonged stay' }, { val: 'Sentinel', color: '#dc2626', desc: 'Death, serious disability, or wrong site surgery' }];

function IncidentTab() {
  const { records: incidents, create, patch } = useHmsRecords('quality-incident');
  const [form, setForm] = useState({ category: INCIDENT_CATEGORIES[0], patient: '', ward: 'ICU', date: new Date().toISOString().slice(0, 10), severity: 'Near Miss', reporter: '', description: '', immediateAction: '' });
  const [showForm, setShowForm] = useState(false);
  const [incBusy, setIncBusy] = useState(false);
  const [incErr, setIncErr] = useState('');
  const [capaDraft, setCapaDraft] = useState({});
  const [rcaDraft, setRcaDraft] = useState({});
  const [capaBusy, setCapaBusy] = useState('');
  const sCols = { 'Near Miss': '#10b981', Minor: '#06b6d4', Moderate: '#f59e0b', Major: '#f97316', Sentinel: '#dc2626' };

  const saveCapa = async (inc) => {
    const id = inc.recordId || inc.id;
    setCapaBusy(id);
    try {
      await patch(id, {
        capa: capaDraft[id] ?? inc.capa ?? '',
        rca: rcaDraft[id] ?? inc.rca ?? '',
        status: 'Under Review',
      });
    } catch (e) {
      window.alert(e.message || 'Could not save CAPA');
    } finally {
      setCapaBusy('');
    }
  };

  const closeIncident = async (inc) => {
    const id = inc.recordId || inc.id;
    if (!window.confirm('Close this incident? CAPA should be complete.')) return;
    setCapaBusy(id);
    try {
      await patch(id, {
        status: 'Closed',
        capa: capaDraft[id] ?? inc.capa ?? '',
        rca: rcaDraft[id] ?? inc.rca ?? '',
        closedAt: new Date().toISOString(),
      });
    } catch (e) {
      window.alert(e.message || 'Could not close incident');
    } finally {
      setCapaBusy('');
    }
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'This Month', val: incidents.length, color: '#6366f1' }, { label: 'Near Misses', val: incidents.filter(i => i.severity === 'Near Miss').length, color: '#10b981' }, { label: 'Sentinel Events', val: incidents.filter(i => i.severity === 'Sentinel').length, color: '#dc2626' }, { label: 'Open CAPAs', val: incidents.filter(i => i.status !== 'Closed').length, color: '#f59e0b' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, marginBottom: 14, fontSize: 13, color: '#92400e' }}>
        📌 NABH requirement: All incidents must be reported within 24 hours. Sentinel events require Root Cause Analysis (RCA) within 45 days.
      </div>
      <div style={{ marginBottom: 12 }}><button type="button" className="btn btn-primary" onClick={() => setShowForm(s => !s)}>{showForm ? '✕ Cancel' : '+ Report Incident'}</button></div>
      {showForm && (
        <div className="card mb form-grid" style={{ borderLeft: '4px solid #f97316' }}>
          <h3 className="full" style={{ marginTop: 0, color: '#f97316' }}>⚠ Incident Report</h3>
          {incErr && <div className="error full">{incErr}</div>}
          <div className="field"><label>Category *</label><select className="input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>{INCIDENT_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></div>
          <div className="field"><label>Patient Involved</label><input className="input" value={form.patient} onChange={e => setForm(f => ({ ...f, patient: e.target.value }))} placeholder="Name or 'N/A'" /></div>
          <div className="field"><label>Ward / Location</label><input className="input" value={form.ward} onChange={e => setForm(f => ({ ...f, ward: e.target.value }))} /></div>
          <div className="field"><label>Date of Incident</label><input type="date" className="input" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
          <div className="field"><label>Severity *</label>
            <select className="input" value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}>
              {SEVERITY_LEVELS.map(s => <option key={s.val}>{s.val}</option>)}
            </select>
          </div>
          <div className="field"><label>Reporter Name</label><input className="input" value={form.reporter} onChange={e => setForm(f => ({ ...f, reporter: e.target.value }))} /></div>
          <div className="field full"><label>Description *</label><textarea className="input" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What happened? Where? When? Who was involved?" /></div>
          <div className="field full"><label>Immediate Action Taken</label><textarea className="input" rows={2} value={form.immediateAction} onChange={e => setForm(f => ({ ...f, immediateAction: e.target.value }))} placeholder="What was done immediately to mitigate harm?" /></div>
          <div className="full form-actions"><button type="button" className="btn btn-primary" onClick={async () => {
            if (!form.description) { setIncErr('Description is required.'); return; }
            setIncBusy(true);
            setIncErr('');
            try {
              await create({ ...form, status: 'Under Review', capa: form.immediateAction || '' });
              setShowForm(false);
            } catch (e) {
              setIncErr(e.message || 'Could not submit incident');
            } finally { setIncBusy(false); }
          }} disabled={incBusy}>{incBusy ? 'Submitting…' : 'Submit Incident Report'}</button></div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {incidents.map(inc => (
          <div key={inc.id} className="card" style={{ borderLeft: `4px solid ${sCols[inc.severity] || '#94a3b8'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              <div>
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#6366f1', fontWeight: 700 }}>{inc.id}</span>
                <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 800 }}>{inc.category}</span>
                <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 999, marginLeft: 8, background: (sCols[inc.severity] || '#94a3b8') + '18', color: sCols[inc.severity] || '#94a3b8', border: `1px solid ${(sCols[inc.severity] || '#94a3b8')}40`, fontWeight: 700 }}>{inc.severity}</span>
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>{inc.date} · {inc.ward}</div>
            </div>
            <p style={{ margin: '0 0 6px', fontSize: 13, color: '#1e293b' }}>{inc.description}</p>
            {inc.immediateAction && (
              <div style={{ fontSize: 12, padding: '6px 10px', background: '#eff6ff', borderRadius: 6, color: '#1d4ed8', marginBottom: 6 }}>
                Immediate: {inc.immediateAction}
              </div>
            )}
            <div className="field" style={{ marginBottom: 6 }}>
              <label style={{ fontSize: 11 }}>CAPA / Corrective action</label>
              <textarea
                className="input"
                rows={2}
                value={capaDraft[inc.recordId || inc.id] ?? inc.capa ?? ''}
                onChange={(e) => setCapaDraft((d) => ({ ...d, [inc.recordId || inc.id]: e.target.value }))}
                placeholder="Root corrective / preventive actions…"
              />
            </div>
            {(inc.severity === 'Major' || inc.severity === 'Sentinel') && (
              <div className="field" style={{ marginBottom: 6 }}>
                <label style={{ fontSize: 11 }}>Root Cause Analysis (RCA)</label>
                <textarea
                  className="input"
                  rows={2}
                  value={rcaDraft[inc.recordId || inc.id] ?? inc.rca ?? ''}
                  onChange={(e) => setRcaDraft((d) => ({ ...d, [inc.recordId || inc.id]: e.target.value }))}
                  placeholder="Why did this happen? Contributing factors…"
                />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: inc.status === 'Closed' ? '#f0fdf4' : '#fffbeb', color: inc.status === 'Closed' ? '#16a34a' : '#92400e', border: `1px solid ${inc.status === 'Closed' ? '#86efac' : '#fde68a'}` }}>{inc.status}</span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>Reported by: {inc.reporter}</span>
              {inc.status !== 'Closed' && (
                <>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={capaBusy === (inc.recordId || inc.id)} onClick={() => saveCapa(inc)}>
                    {capaBusy === (inc.recordId || inc.id) ? 'Saving…' : 'Save CAPA'}
                  </button>
                  <button type="button" className="btn btn-primary btn-sm" disabled={capaBusy === (inc.recordId || inc.id)} onClick={() => closeIncident(inc)}>Close</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NabhAuditTab() {
  const { records: audits, create, loading } = useHmsRecords('nabh-audit');
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ standardCode: 'ACC', score: 0, auditedBy: '', notes: '', checklist: NABH_CHECKLIST.map((c) => ({ ...c })) });

  const latestByStandard = useMemo(() => {
    const map = {};
    [...audits]
      .sort((a, b) => (b.auditedAt || b.createdAt || '').localeCompare(a.auditedAt || a.createdAt || ''))
      .forEach((a) => { if (!map[a.standardCode]) map[a.standardCode] = a; });
    return map;
  }, [audits]);

  const standardsWithScores = NABH_STANDARDS.map((s) => ({
    ...s,
    score: latestByStandard[s.code]?.score ?? null,
    audit: latestByStandard[s.code],
  }));
  const scored = standardsWithScores.filter((s) => s.score != null);
  const overall = scored.length ? Math.round(scored.reduce((s, n) => s + n.score, 0) / scored.length) : null;

  const submitAudit = async () => {
    setBusy(true);
    try {
      await create({
        standardCode: form.standardCode,
        standardName: NABH_STANDARDS.find((s) => s.code === form.standardCode)?.name,
        score: Number(form.score),
        checklist: form.checklist,
        auditedBy: form.auditedBy,
        notes: form.notes,
        auditedAt: new Date().toISOString(),
      });
      setShowForm(false);
    } finally { setBusy(false); }
  };

  return (
    <div>
      {loading && <p className="hint mb">Loading audit records…</p>}
      <div className="info-banner mb">
        Record internal NABH audits below. Upload policy evidence in Document Management. No scores are shown until you save an audit.
      </div>
      <div style={{ marginBottom: 14 }}>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>{showForm ? '✕ Cancel' : '+ Record Internal Audit'}</button>
      </div>
      {showForm && (
        <div className="card mb form-grid" style={{ borderLeft: '4px solid #6366f1' }}>
          <h3 className="full" style={{ marginTop: 0, color: '#6366f1' }}>🏅 NABH Internal Audit</h3>
          <div className="field"><label>Standard</label>
            <select className="input" value={form.standardCode} onChange={(e) => setForm((f) => ({ ...f, standardCode: e.target.value }))}>
              {NABH_STANDARDS.map((s) => <option key={s.code} value={s.code}>{s.code} — {s.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Compliance Score (%)</label><input type="number" min={0} max={100} className="input" value={form.score} onChange={(e) => setForm((f) => ({ ...f, score: e.target.value }))} /></div>
          <div className="field"><label>Audited By</label><input className="input" value={form.auditedBy} onChange={(e) => setForm((f) => ({ ...f, auditedBy: e.target.value }))} /></div>
          <div className="field full"><label>Notes</label><textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
          <div className="full">
            <label style={{ fontSize: 12, fontWeight: 700 }}>Checklist (toggle met / not met)</label>
            {form.checklist.map((item, i) => (
              <label key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!item.met}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    checklist: f.checklist.map((c, j) => (j === i ? { ...c, met: e.target.checked } : c)),
                  }))}
                />
                <span>{item.item}</span>
              </label>
            ))}
          </div>
          <div className="full form-actions"><button type="button" className="btn btn-primary" disabled={busy} onClick={submitAudit}>{busy ? 'Saving…' : '✓ Save Audit'}</button></div>
        </div>
      )}
      {overall != null ? (
        <div style={{ padding: '14px 18px', borderRadius: 12, background: overall >= 80 ? '#f0fdf4' : '#fef2f2', border: `2px solid ${scoreColor(overall)}40`, marginBottom: 16 }}>
          <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 40, fontWeight: 900, color: scoreColor(overall) }}>{overall}%</div>
          <div style={{ fontWeight: 700, color: '#475569' }}>Overall NABH Compliance Score</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>From {scored.length} audited standard(s) · {overall >= 85 ? '🟢 Ready for Assessment' : '🟡 Improvements Needed'}</div>
        </div>
      ) : (
        <p className="hint table-empty mb">No internal audits recorded yet. Use &quot;Record Internal Audit&quot; to score each NABH standard.</p>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>NABH Standard-wise Scores</h3>
          {standardsWithScores.map((s) => (
            <div key={s.code} onClick={() => setSelected(selected?.code === s.code ? null : s)} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <div><span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#6366f1', marginRight: 8 }}>{s.code}</span><span style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</span></div>
                <span style={{ fontWeight: 900, color: s.score != null ? scoreColor(s.score) : '#94a3b8', fontFamily: 'Outfit,sans-serif', fontSize: 16 }}>{s.score != null ? `${s.score}%` : '—'}</span>
              </div>
              {s.score != null && (
                <div style={{ background: '#f1f5f9', borderRadius: 4, height: 6 }}><div style={{ width: `${s.score}%`, background: scoreColor(s.score), height: 6, borderRadius: 4 }} /></div>
              )}
              {selected?.code === s.code && s.audit && (
                <div style={{ marginTop: 10, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>Last audit — {new Date(s.audit.auditedAt || s.audit.createdAt).toLocaleDateString('en-IN')} by {s.audit.auditedBy || '—'}</div>
                  {(s.audit.checklist || NABH_CHECKLIST).map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', fontSize: 13 }}>
                      <span style={{ fontSize: 14, color: item.met ? '#10b981' : '#dc2626' }}>{item.met ? '✓' : '✗'}</span>
                      <span style={{ color: item.met ? '#1e293b' : '#dc2626' }}>{item.item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div>
          <div className="card mb">
            <h3 style={{ marginTop: 0 }}>Recent Audits</h3>
            {audits.length === 0 && <p className="hint">No audits yet.</p>}
            {[...audits].sort((a, b) => (b.auditedAt || b.createdAt || '').localeCompare(a.auditedAt || a.createdAt || '')).slice(0, 8).map((a) => (
              <div key={a.recordId || a.id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                <strong>{a.standardCode}</strong> — {a.score}% · {new Date(a.auditedAt || a.createdAt).toLocaleDateString('en-IN')}
              </div>
            ))}
          </div>
          {scored.filter((s) => s.score < 85).length > 0 && (
            <div className="card">
              <h3 style={{ marginTop: 0, color: '#f97316' }}>⚡ Areas Needing Attention</h3>
              {scored.filter((s) => s.score < 85).sort((a, b) => a.score - b.score).map((s) => (
                <div key={s.code} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{s.code} — {s.name}</span>
                    <span style={{ fontWeight: 900, color: scoreColor(s.score), fontFamily: 'Outfit,sans-serif' }}>{s.score}%</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QualityIndicatorsTab() {
  const { records: incidents } = useHmsRecords('quality-incident', 200);
  const { records: feedbacks } = useHmsRecords('feedback', 200);
  const { records: neonates } = useHmsRecords('nicu-neonate', 200);
  const { records: handHygieneObs } = useHmsRecords('infection-handhygiene', 200);
  const { data: bedData, loading: bedsLoading } = useLiveData(() => ipApi.getBeds(), []);
  const { data: admData, loading: admLoading } = useLiveData(() => ipApi.getAdmissions(200), []);
  const { data: allAdmData, loading: allAdmLoading } = useLiveData(() => ipApi.getAdmissions(300, { includeDischarged: true }), []);
  const { data: labData, loading: labLoading } = useLiveData(() => ipApi.getLabOrders(200), []);

  const KQIs = useMemo(() => buildKQIs({
    beds: bedData?.beds || [],
    admissions: admData?.admissions || [],
    allAdmissions: allAdmData?.admissions || [],
    incidents,
    feedbacks,
    labOrders: labData?.orders || [],
    neonates,
    handHygieneObs,
  }), [bedData, admData, allAdmData, incidents, feedbacks, labData, neonates, handHygieneObs]);

  const loading = bedsLoading || admLoading || allAdmLoading || labLoading;
  const liveCount = KQIs.filter((k) => k.live).length;

  const statusLabel = (k) => {
    if (k.status === 'na') return '— No data';
    if (k.status === 'bad') return '✗ Critical';
    if (k.status === 'ok') return '✓ On Target';
    return '⚠ Needs Attention';
  };

  return (
    <div>
      {loading && <p className="hint mb">Loading live indicators…</p>}
      <div className="info-banner mb">
        {liveCount} of {KQIs.length} indicators computed from live beds, admissions, and incident reports. Add incidents and audits for complete quality tracking.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'On Target', val: KQIs.filter((k) => k.status === 'ok').length, color: '#10b981' }, { label: 'Need Attention', val: KQIs.filter((k) => k.status === 'warn' || k.status === 'bad').length, color: '#f59e0b' }, { label: 'Live Indicators', val: liveCount, color: '#6366f1' }].map((s) => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 24, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Key Quality Indicators (KQIs)</h3>
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Indicator</th><th>Current Value</th><th>Target</th><th>Department</th><th>Frequency</th><th>Status</th></tr></thead>
            <tbody>
              {KQIs.map((k) => (
                <tr key={k.indicator}>
                  <td style={{ fontWeight: 600, fontSize: 13 }}>{k.indicator}{k.live && <span style={{ marginLeft: 6, fontSize: 9, color: '#0d6e6a', fontWeight: 800 }}>LIVE</span>}</td>
                  <td style={{ fontWeight: 800, fontFamily: 'Outfit,sans-serif', color: k.status === 'ok' ? '#10b981' : k.status === 'na' ? '#94a3b8' : '#f97316' }}>{k.value}</td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{k.target}</td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{k.dept}</td>
                  <td style={{ fontSize: 11, color: '#94a3b8' }}>{k.frequency}</td>
                  <td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: k.status === 'ok' ? '#f0fdf4' : k.status === 'na' ? '#f8fafc' : '#fffbeb', color: k.status === 'ok' ? '#16a34a' : k.status === 'na' ? '#94a3b8' : '#92400e', border: `1px solid ${k.status === 'ok' ? '#86efac' : k.status === 'na' ? '#e2e8f0' : '#fde68a'}` }}>{statusLabel(k)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MockDrillTab() {
  const { records: drills, create, patch, loading } = useHmsRecords('quality-drill');
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    type: 'Fire Drill', date: new Date().toISOString().slice(0, 10),
    location: 'Main Building', participants: '', findings: '', actions: '', status: 'Completed',
  });

  const submit = async () => {
    setBusy(true);
    try {
      await create({ ...form, conductedAt: new Date().toISOString() });
      setShowForm(false);
    } finally { setBusy(false); }
  };

  return (
    <div>
      {loading && <p className="hint mb">Loading drills…</p>}
      <div className="info-banner mb">Log mock drills (fire, code blue, evacuation) for NABH evidence. Attach photos via Document Management.</div>
      <div style={{ marginBottom: 12 }}>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Cancel' : '+ Log Mock Drill'}</button>
      </div>
      {showForm && (
        <div className="card mb form-grid" style={{ borderLeft: '4px solid #0d6e6a' }}>
          <div className="field"><label>Drill type</label>
            <select className="input" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
              {['Fire Drill', 'Code Blue', 'Evacuation', 'Disaster', 'Infection Outbreak'].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>Date</label><input type="date" className="input" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></div>
          <div className="field"><label>Location</label><input className="input" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} /></div>
          <div className="field"><label>Participants</label><input className="input" value={form.participants} onChange={(e) => setForm((f) => ({ ...f, participants: e.target.value }))} placeholder="Count / depts" /></div>
          <div className="field full"><label>Findings</label><textarea className="input" rows={2} value={form.findings} onChange={(e) => setForm((f) => ({ ...f, findings: e.target.value }))} /></div>
          <div className="field full"><label>Actions</label><textarea className="input" rows={2} value={form.actions} onChange={(e) => setForm((f) => ({ ...f, actions: e.target.value }))} /></div>
          <div className="full form-actions"><button type="button" className="btn btn-primary" disabled={busy} onClick={submit}>{busy ? 'Saving…' : 'Save Drill'}</button></div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {drills.length === 0 && <p className="hint">No drills logged yet.</p>}
        {drills.map((d) => (
          <div key={d.recordId || d.id} className="card" style={{ borderLeft: '4px solid #0d6e6a' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <strong>{d.type}</strong>
              <span style={{ fontSize: 12, color: '#64748b' }}>{d.date} · {d.location}</span>
            </div>
            {d.findings && <p style={{ fontSize: 13, margin: '8px 0 0' }}>{d.findings}</p>}
            {d.actions && <div style={{ fontSize: 12, marginTop: 6, color: '#16a34a' }}>Actions: {d.actions}</div>}
            {d.status !== 'Closed' && (
              <button type="button" className="btn btn-sm btn-secondary" style={{ marginTop: 8 }} onClick={() => patch(d.recordId || d.id, { status: 'Closed' })}>Mark closed</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function QualityPage() {
  const [tab, setTab] = useState('incidents');
  return (
    <PageShell
      title="Quality / NABH Audit"
      subtitle="Incident Reporting · NABH Standards · Quality Indicators · CAPA Tracking · Mock Drills"
      actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => window.print()}>📊 Quality Report</button>}
    >
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[
          { id: 'incidents', label: '⚠ Incident Reporting' },
          { id: 'nabh', label: '🏅 NABH Audit' },
          { id: 'kqi', label: '📊 Quality Indicators' },
          { id: 'drills', label: '🚒 Mock Drills' },
        ].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'incidents' && <IncidentTab />}
      {tab === 'nabh' && <NabhAuditTab />}
      {tab === 'kqi' && <QualityIndicatorsTab />}
      {tab === 'drills' && <MockDrillTab />}
    </PageShell>
  );
}
